import type Badge from '$lib/components/atoms/badge/Badge.svelte';
import type {
	AccommodationMichelinPoiData,
	AccommodationPoiData,
	GasStationPoiData,
	ParkingPoiData,
	Partner1PoiData,
	Partner2PoiData,
	PicturePoiCardScheme,
	PoiData,
	PoiFilterOptionId,
	RestaurantMichelinPoiData,
	RestaurantPoiData,
	RestStopPoiData,
	ServiceEVPoiData,
	TourismPoiData,
} from '$lib/components/pois/types';
import type { ComponentProps } from 'svelte';
import type { nonHRTBadgeColor } from './constant';
import type { PoiCardFactory, PoiCardListScheme, PoiCardSize } from './types';
import { getSponsorConfigByPoi } from '$lib/constants/sponsoredPoi';
import { cardScheme } from './constant';
import {
	buildAccommodationDistinctions,
	buildAccommodationMichelinDistinction,
	buildAccommodationPriceTag,
	buildAccomodationsFooterLink,
	buildGasStationBadgeDot,
	buildGasStationContent,
	buildHeroBadge,
	buildNonHRTPoisBadge,
	buildPoiHRTImageOrCarousel,
	buildPriceTagPriceRange,
	buildRestaurantMichelinDistinctions,
	buildRestaurantPriceTag,
	buildRestStopHighlight,
	buildRestStopSubtitle,
	buildScoreBadge,
	buildTheForkFooter,
	buildTourismDistinctions,
	getFuelsAndGasStationFiltersAndLastUpdated,
} from './factories-utils';

interface FactoryArgs<T> {
	poi: T;
	href: string;
	size: PoiCardSize;
	index: number;
	scheme: PoiCardListScheme;
}

export function buildPoiCardProps(poi: PoiData, href: string, index: number, size: PoiCardSize = 'lg'): PoiCardFactory | undefined {
	const scheme = cardScheme[poi.__typename as Exclude<PoiData['__typename'], 'PartnerPOI' | 'Partner2POI'>];

	switch (poi.__typename) {
		case 'AccommodationPOI':
			return accommodationPoiFactory({ poi, href, size, index, scheme });
		case 'AccommodationMichelinPOI':
			return accommodationMichelinPoiFactory({ poi, href, size, index, scheme });
		case 'RestaurantPOI':
			return restaurantPoiFactory({ poi, href, size, index, scheme });
		case 'RestaurantMichelinPOI':
			return restaurantMichelinPoiFactory({ poi, href, size, index, scheme });
		case 'TourismPOI':
			return tourismPoiFactory({ poi, href, size, index, scheme });
		case 'ServiceEVPOI':
			return evStationFactory({ poi, href, size, index, scheme });
		case 'ServiceGasStationPOI':
			return gasStationFactory({ poi, href, size, index, scheme });
		case 'ParkingPOI':
			return parkingFactory({ poi, href, size, index, scheme });
		case 'ServiceRestStopPOI':
			return restStopFactory({ poi, href, size, index, scheme });
		case 'PartnerPOI':
			return partnerPoiFactory({ poi, href, size, index, scheme });
		case 'Partner2POI':
			return partnerPoiFactory({ poi, href, size, index, scheme });
		default:
			return undefined;
	}
}

function accommodationPoiFactory({ poi, href, size, index, scheme }: FactoryArgs<AccommodationPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: buildAccommodationPriceTag(poi),
		},
		content: {
			title: {
				title: poi.name,
				badge: buildScoreBadge(poi),
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildAccommodationDistinctions(poi, scheme as PicturePoiCardScheme),
		},
		footer: {
			link: buildAccomodationsFooterLink(poi, scheme as PicturePoiCardScheme),
		},
	} satisfies PoiCardFactory;
}

function partnerPoiFactory({ poi, href, size, index, scheme }: FactoryArgs<Partner1PoiData | Partner2PoiData>) {
	const sponsoredPoiConfig = getSponsorConfigByPoi(poi);
	const badge = {
		imgUrl: sponsoredPoiConfig?.badge.imgUrl,
		isSquare: sponsoredPoiConfig?.badge.isSquare,
		colorStyles: sponsoredPoiConfig?.badge.style,
		iconPosition: 'right',
	} satisfies ComponentProps<typeof Badge>;

	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				badge,
			},
			city: poi?.address?.city ?? undefined,
		},
	} satisfies PoiCardFactory;
}

function accommodationMichelinPoiFactory({ poi, href, size, index, scheme }: FactoryArgs<AccommodationMichelinPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: buildAccommodationPriceTag(poi),
		},
		content: {
			title: {
				title: poi.name,
				badge: buildScoreBadge(poi),
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildAccommodationMichelinDistinction(poi, scheme as PicturePoiCardScheme),
		},
		footer: {
			link: buildAccomodationsFooterLink(poi, scheme as PicturePoiCardScheme),
		},
	} satisfies PoiCardFactory;
}

function restaurantMichelinPoiFactory({ poi, href, size, index, scheme }: FactoryArgs<RestaurantMichelinPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: {
				priceRange: buildPriceTagPriceRange(poi),
			},
		},
		content: {
			title: {
				title: poi.name,
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildRestaurantMichelinDistinctions(poi),
		},
		footer: {
			theFork: buildTheForkFooter(poi),
		},
	} satisfies PoiCardFactory;
}

function restaurantPoiFactory({ poi, href, size, index, scheme }: FactoryArgs<RestaurantPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
			priceTag: buildRestaurantPriceTag(poi.averagePrice),
		},
		content: {
			title: {
				title: poi.name,
				badge: buildScoreBadge(poi),
			},
			city: poi?.address?.city ?? undefined,
		},
		footer: {
			theFork: buildTheForkFooter(poi),
		},
	} satisfies PoiCardFactory;
}

function tourismPoiFactory({ poi, href, size, index, scheme }: FactoryArgs<TourismPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		hero: {
			...buildPoiHRTImageOrCarousel(poi, index, scheme as PicturePoiCardScheme),
			badge: buildHeroBadge(scheme as PicturePoiCardScheme),
		},
		content: {
			title: {
				title: poi.name,
			},
			city: poi?.address?.city ?? undefined,
			distinction: buildTourismDistinctions(poi, scheme as PicturePoiCardScheme),
		},
	} satisfies PoiCardFactory;
}

function evStationFactory({ poi, href, index, size, scheme }: FactoryArgs<ServiceEVPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				clamp: 'truncate',
				badge: buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor),
			},
			city: poi?.address?.city ?? undefined,
			evStation: {
				spotsCount: poi.spots_count,
				connectors: poi.connectors,
			},
		},
	} satisfies PoiCardFactory;
}

function gasStationFactory({ poi, href, index, size, scheme }: FactoryArgs<GasStationPoiData>) {
	const { fuelPricesMap, gasStationTypeFilter, lastUpdated } = getFuelsAndGasStationFiltersAndLastUpdated(poi);
	const { class: cls, ...rest } = buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor);
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				badge: {
					...rest,
					class: `${cls} ${buildGasStationBadgeDot(fuelPricesMap, gasStationTypeFilter as unknown as PoiFilterOptionId[])}`,
				},
			},
			city: poi?.address?.city ?? poi?.address?.formattedAddress ?? undefined,
			gasStation: buildGasStationContent(fuelPricesMap, gasStationTypeFilter as unknown as PoiFilterOptionId[], lastUpdated),
		},
	} satisfies PoiCardFactory;
}

function parkingFactory({ poi, href, index, size, scheme }: FactoryArgs<ParkingPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				badge: buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor),
			},
			city: poi?.address?.city ?? undefined,
			parking: {
				address: poi.splittedAddress,
			},
		},
	} satisfies PoiCardFactory;
}

function restStopFactory({ poi, href, index, size, scheme }: FactoryArgs<RestStopPoiData>) {
	return {
		href,
		size,
		index,
		scheme,
		tag: 'li',
		content: {
			title: {
				title: poi.name,
				clamp: 'truncate',
				badge: buildNonHRTPoisBadge(scheme as keyof typeof nonHRTBadgeColor),
			},
			city: poi?.address?.city ?? undefined,
			restStop: {
				...buildRestStopSubtitle(poi),
				...buildRestStopHighlight(poi),
			},
		},
	} satisfies PoiCardFactory;
}
